import { useState, useEffect } from 'react';
import './App.css';
import Navbar from './components/Navbar';
import LogicApps from './components/LogicApps';
import Setup from './components/Setup';
import ApiDocs from './components/ApiDocs';
import ApiDocsViewer from './components/ApiDocsViewer';
import About from './components/About';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';

const PATH_TO_TAB = { '/api-docs': 'apidocs', '/setup': 'setup', '/about': 'about' };
const TAB_TO_PATH = { apidocs: '/api-docs', setup: '/setup', about: '/about', logicapps: '/' };

function tabFromPath(path) {
  return PATH_TO_TAB[path] || 'logicapps';
}

function App() {
  const [activeTab, setActiveTab] = useState(() => tabFromPath(window.location.pathname));

  const navigate = (tab) => {
    const path = TAB_TO_PATH[tab] || '/';
    window.history.pushState({ tab }, '', path);
    setActiveTab(tab);
  };

  useEffect(() => {
    const onPop = (e) => setActiveTab(e.state?.tab || tabFromPath(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Public docs viewer — no login required, URL must contain ?share=<id> or ?url=<yaml-url>
  const searchParams = new URLSearchParams(window.location.search);
  const shareId         = searchParams.get('share');
  const publicDocUrl    = searchParams.get('url');
  const publicDocViewer = searchParams.get('viewer');
  if (window.location.pathname.startsWith('/docs') && (shareId || publicDocUrl)) {
    return <ApiDocsViewer shareId={shareId} url={publicDocUrl} viewer={publicDocViewer} />;
  }

  const apiCall = async (path, method = 'GET', body = null) => {
    const apiUrl      = process.env.REACT_APP_API_URL || 'http://localhost:7071';
    const functionKey = process.env.REACT_APP_API_KEY  || '';
    const separator   = path.includes('?') ? '&' : '?';
    const qs          = functionKey ? `${separator}code=${functionKey}` : '';
    const options     = { method };
    if (body !== null) {
      options.headers = { 'Content-Type': 'application/json' };
      options.body    = JSON.stringify(body);
    }
    const res  = await fetch(`${apiUrl}/api/${path}${qs}`, options);
    const text = await res.text();
    if (!res.ok) throw new Error(text || `HTTP ${res.status}`);
    return text ? JSON.parse(text) : null;
  };

  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:7071';
  const apiKey     = process.env.REACT_APP_API_KEY  || '';

  return (
    <div className="App">
      <AuthenticatedTemplate>
        <div className="page-wrapper">
          <Navbar activeTab={activeTab} onNavigate={navigate} />

          <div className="page-hero">
            <p className="page-hero-eyebrow">Astena Integration Hub</p>
          </div>

          <main className="main-wrapper">
            {activeTab === 'about' ? (
              <About />
            ) : (
              <div className="padding-global">
                <div className="container-large">
                  {activeTab === 'logicapps' && (
                    <LogicApps apiCall={apiCall} />
                  )}
                  {activeTab === 'setup' && (
                    <Setup />
                  )}
                  {activeTab === 'apidocs' && (
                    <ApiDocs
                      apiCall={apiCall}
                      apiBaseUrl={apiBaseUrl}
                      apiKey={apiKey}
                      onClose={() => navigate('logicapps')}
                    />
                  )}
                </div>
              </div>
            )}
          </main>
        </div>
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <div className="login-container">
          <img
            src="https://cdn.prod.website-files.com/67e26aef79a7047719ea9db8/67e28ceff1544e195a3a2269_astena.svg"
            alt="Astena Logo"
            className="login-logo"
          />
        </div>
      </UnauthenticatedTemplate>
    </div>
  );
}

export default App;
